/// <amd-module name="Build/Settings/retention/RetentionSettingsView" />
import * as React from "react";
import * as Resources from "Build/Settings/Resources";

import { RetentionSettingData } from "Build/Client/Contracts/DataProvider";
import { RetentionSettingsStore } from "./RetentionSettingsStore";

import { ObservableValue } from "VSS/Core/Observable";
import { format } from "VSS/Core/Util/String";
import { ErrorBoundary } from "VSS/Platform/Components/ErrorBoundary";
import { IVssComponentContext, IVssComponentProperties, RegionComponent, VssComponent } from "VSS/Platform/Layout";
import { IVssPerformanceService } from "VSS/Platform/Performance";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Spinner } from "VSSUI/Spinner";
import { FormItem } from "VSSUI/FormItem";
import { Icon } from "VSSUI/Icon";
import { Link } from "VSSUI/Link";
import { TextField } from "VSSUI/TextField";

interface IRetentionSettingsViewState {
    isReadOnly: boolean;
    purgeArtifactsIgnored: boolean;
    purgeArtifactsInvalid: boolean;
    purgeArtifactsMessage: string;
    purgePullRequestRunsInvalid: boolean;
    purgePullRequestRunsMessage: string;
    purgeRunsInvalid: boolean;
    purgeRunsMessage: string;
    ready: boolean;
    runsToRetainPerProtectedBranchMessage: string;
    settingsValid: boolean;
    showSettings: boolean;
}

const retentionDocsUri = "https://go.microsoft.com/fwlink/?linkid=2114007";

export class RetentionSettingsView extends VssComponent<IVssComponentProperties, IRetentionSettingsViewState> {
    private _store: RetentionSettingsStore;

    constructor(props: IVssComponentProperties, context: IVssComponentContext) {
        super(props, context);

        this.state = {
            isReadOnly: true,
            purgeArtifactsIgnored: false,
            purgeArtifactsInvalid: false,
            purgeArtifactsMessage: "",
            purgePullRequestRunsInvalid: false,
            purgePullRequestRunsMessage: "",
            purgeRunsInvalid: false,
            purgeRunsMessage: "",
            ready: false,
            runsToRetainPerProtectedBranchMessage: "",
            settingsValid: false,
            showSettings: true
        };

        this._store = new RetentionSettingsStore({
            pageContext: context.pageContext
        });

        this._store.fetch();
    }

    public componentDidMount() {
        this._store.addListener(this._onStoreChanged);

        const perfService = this.context.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
        perfService.markTimeToInteractive("Admin", "RetentionSettings");
    }

    public componentWillUnmount() {
        this._store.removeListener(this._onStoreChanged);
    }

    public render(): JSX.Element {
        if (this.state.ready) {
            if (this.state.showSettings) {
                return <div className="settings-view flex-column rhythm-vertical-8">
                    {this.getPurgeArtifactsIgnoredMessageRow(this.state.purgeArtifactsIgnored)}
                    {this.getRetentionRow(Resources.DaysToKeepArtifacts, this._store.purgeArtifactsInvalid, this._store.purgeArtifactsAfter, this.state.purgeArtifactsMessage)}
                    {this.getRetentionRow(Resources.DaysToKeepRuns, this._store.purgeRunsInvalid, this._store.purgeRunsAfter, this.state.purgeRunsMessage)}
                    {this.getRetentionRow(Resources.DaysToKeepPullRequestRuns, this._store.purgePullRequestRunsInvalid, this._store.purgePullRequestRunsAfter, this.state.purgePullRequestRunsMessage)}
                    {this.getRetentionRow(
                        Resources.NumberOfRecentRunsPerPipeline,
                        this._store.runsToRetainPerProtectedBranchInvalid,
                        this._store.runsToRetainPerProtectedBranch,
                        this.state.runsToRetainPerProtectedBranchMessage,
                        Resources.RunsPerPipelineMoreInfo
                    )}
                    <Link className="is-link margin-top-16" href={retentionDocsUri} target="_blank">{Resources.LearnMore}</Link>
                    <div className="clear" key="clear" />
                </div>
            }

            return this._noContent();
        }

        return (
            <Spinner className="loading-spinner" label={Resources.LoadingText} key="spinner" />
        );
    }

    private _noContent = () => {
        return (
            <ErrorBoundary handlerComponentType="tfs.page-error-handler">
                <RegionComponent
                    componentRegion="retention-policy-tab-content-region"
                    fallbackComponent={() => (
                        <div>
                            {Resources.RetentionPolicyNoPermissions}
                        </div>
                    )}
                />
            </ErrorBoundary>
        );
    }

    private _onStoreChanged = () => {
        this.setState({
            isReadOnly: this._store.isReadOnly,
            purgeArtifactsIgnored: this._store.purgeArtifactsIgnored,
            purgeArtifactsInvalid: this._store.purgeArtifactsInvalid,
            purgeArtifactsMessage: this._store.purgeArtifactsInvalid ? this.getRequiredRangeMessage(this._store.purgeArtifactsSetting) : "",
            purgePullRequestRunsInvalid: this._store.purgePullRequestRunsInvalid,
            purgePullRequestRunsMessage: this._store.purgePullRequestRunsInvalid ? this.getRequiredRangeMessage(this._store.purgePullRequestRunsSetting) : "",
            purgeRunsInvalid: this._store.purgeRunsInvalid,
            purgeRunsMessage: this._store.purgeRunsInvalid ? this.getRequiredRangeMessage(this._store.purgeRunsSetting) : "",
            ready: true,
            runsToRetainPerProtectedBranchMessage: this._store.runsToRetainPerProtectedBranchInvalid ? this.getRequiredRangeMessage(this._store.runsToRetainPerProtectedBranchSetting) : "",
            settingsValid: this._store.isValid,
            showSettings: this._store.showSettings
        });
    }

    private getPurgeArtifactsIgnoredMessageRow(artifactsIgnored: boolean) {
        if (artifactsIgnored) {
            return (
                <div key="ignored">
                    <div className="retention-settings-row">
                        <MessageCard
                            className="retention-settings-message"
                            severity={MessageCardSeverity.Warning}>
                            {Resources.PurgeArtifactsSettingIgnored}
                        </MessageCard>
                    </div>
                </div>
            );
        }

        return <span key="ignored" />;
    }

    private getRetentionRow(label: string, isInvalid: boolean, value: ObservableValue<string>, errorMessage: string, tooltip?: string) {
        return (
            <div key={label}>
                <div className="retention-settings-row flex-row flex-start">
                    <div className="retention-settings-label flex-center flex-row margin-vertical-8">
                        {label}
                        {tooltip &&
                            <Icon
                                className="more-info-icon"
                                iconName="Info"
                                tooltipProps={{ text: tooltip }}
                                tabIndex={0}
                            />
                        }
                    </div>
                    <FormItem
                        className="retention-settings-value"
                        error={isInvalid}
                        message={errorMessage}
                    >
                        <TextField
                            readOnly={this.state.isReadOnly}
                            value={value}
                            onChange={(e, newValue) => value.value = newValue}
                        />
                    </FormItem>
                </div>
            </div>
        );
    }

    private getRequiredRangeMessage(settingsValue: RetentionSettingData): string {
        return format(Resources.RequiredValueRangeError, settingsValue.min, settingsValue.max);
    }
}
